import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const REMARK_PLUGINS = [remarkGfm]
const MAX_EPHEMERAL_LENGTH = 1000

interface ProcessingIndicatorProps {
  hasActivity?: boolean
  streamingText?: string | null
  isPmStreaming?: boolean
  activityText?: string | null
}

export function ProcessingIndicator({ hasActivity = false, streamingText, isPmStreaming = false, activityText }: ProcessingIndicatorProps) {
  const { t } = useTranslation('conversations')
  const displayText = activityText || (hasActivity ? t('chat.building') : t('chat.thinking'))

  // Show status text only after 2s delay if nothing is streaming.
  // Activity text (from tool calls) shows immediately.
  const isAnyStreaming = !!streamingText || isPmStreaming
  const hasActivityText = !!activityText
  const [showStatusText, setShowStatusText] = useState(false)
  useEffect(() => {
    if (hasActivityText) {
      setShowStatusText(true)
      return
    }
    if (isAnyStreaming) {
      setShowStatusText(false)
      return
    }
    const timer = setTimeout(() => setShowStatusText(true), 2000)
    return () => clearTimeout(timer)
  }, [isAnyStreaming, hasActivityText])

  return (
    <div className="mt-4">
      {/* Circle icon + "Building..." (hidden until 2s with no streaming, hidden when streaming) */}
      <div className="flex gap-2 animate-pulse">
        <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
        </div>
        {showStatusText && !streamingText && (
          <div className="flex items-baseline gap-0.5 pt-1.5">
            <span className="text-sm text-muted-foreground">{displayText}</span>
            <div className="flex items-center gap-0.5">
              <div className="h-0.5 w-0.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="h-0.5 w-0.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="h-0.5 w-0.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Spawned chunk — L-connector then shimmering markdown */}
      {streamingText && (
        <div className="flex items-start ml-[7px] mt-2 animate-in fade-in duration-300">
          <span className="text-blue-500/30 shrink-0 text-lg leading-2 mr-4">└</span>
          <div className="shimmer-text text-sm min-w-0 -mt-2 max-w-none [&_p]:m-0 [&_pre]:hidden [&_table]:hidden [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm [&_code]:text-xs">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
              {streamingText.length > MAX_EPHEMERAL_LENGTH
                ? streamingText.slice(0, MAX_EPHEMERAL_LENGTH) + '...'
                : streamingText}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
